import { Bell, ChevronRight } from "lucide-react";
import { UPLOAD_URL } from "../utils/constants";
import { getGenderPlaceholder } from "../helpers/matches.helper";

type ForegroundNotificationToastProps = {
  title?: string | null;
  body?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  gender?: string | null;
  badge?: string | null;
  onOpen?: () => void;
};

const isRemoteUrl = (value?: string | null) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

const resolveImageSource = ({
  image,
  imageUrl,
  gender,
}: Pick<
  ForegroundNotificationToastProps,
  "image" | "imageUrl" | "gender"
>) => {
  if (isRemoteUrl(imageUrl)) return imageUrl!.trim();
  if (isRemoteUrl(image)) return image!.trim();
  if (typeof image === "string" && image.trim()) {
    return `${UPLOAD_URL}/users/profile_pic/${image.trim().replace(/^\/+/, "")}`;
  }
  return getGenderPlaceholder(gender || "");
};

const ForegroundNotificationToast = ({
  title,
  body,
  image,
  imageUrl,
  gender,
  badge,
  onOpen,
}: ForegroundNotificationToastProps) => {
  const resolvedTitle = title || "New profile notification";
  const resolvedBody = body || "Tap to view details";
  const resolvedImage = resolveImageSource({ image, imageUrl, gender });

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 rounded-[22px] border border-fuchsia-100 bg-white/95 px-3 py-3 text-left shadow-[0_18px_48px_rgba(190,24,93,0.16)] backdrop-blur"
    >
      <div className="relative shrink-0">
        <img
          src={resolvedImage}
          alt={resolvedTitle}
          className="h-14 w-14 rounded-2xl object-cover ring-2 ring-white"
          onError={(event) => {
            event.currentTarget.src = getGenderPlaceholder(gender || "");
          }}
        />
        <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-600 text-white shadow-lg">
          <Bell className="h-3.5 w-3.5" />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold text-slate-900">
              {resolvedTitle}
            </p>
            <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-slate-600">
              {resolvedBody}
            </p>
          </div>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
        </div>

        {badge ? (
          <div className="mt-2 inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-rose-700">
            {badge}
          </div>
        ) : null}
      </div>
    </button>
  );
};

export default ForegroundNotificationToast;
